import React, { useState } from 'react';
import { getReplyList } from '../../../api';
import CommentInput from './CommentInput';

export interface CommentData {
    id: string;
    writerNickname: string;
    writerEmail?: string;
    content: string;
    createdAt: string;
    isDeleted: boolean;
    status?: 'NORMAL' | 'DELETED_BY_USER' | 'HIDDEN_BY_ADMIN' | 'BLOCKED';
    likeCount: number;
    replyCount?: number;
    isLiked: boolean;
    isMine: boolean;
    mine?: boolean;
    authorStatus?: boolean;
    authorProfileImg?: string;
    children?: CommentData[];
}

interface CommentItemProps {
    comment: CommentData;
    onReply: (parentId: string, content: string) => Promise<void>;
    onDelete: (commentId: string) => Promise<void>;
    onReport: (commentId: string) => Promise<void>;
    onLike: (commentId: string) => Promise<void>;
    isChild?: boolean;
}

const HeartIcon = ({ filled }: { filled: boolean }) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
);

const DotsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
        <circle cx="12" cy="5" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="12" cy="19" r="1.5" />
    </svg>
);

const DefaultAvatar = () => (
    <div className="w-full h-full flex items-center justify-center bg-[#ebebeb] text-[#c0c0c0]">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
        </svg>
    </div>
);

const CommentItem: React.FC<CommentItemProps> = ({
    comment,
    onReply,
    onDelete,
    onReport,
    onLike,
    isChild = false
}) => {
    const [isReplying, setIsReplying] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [replies, setReplies] = useState<CommentData[]>(comment.children || []);
    const [totalRepliesCount, setTotalRepliesCount] = useState(comment.replyCount || 0);
    const [repliesLoaded, setRepliesLoaded] = useState(false);

    React.useEffect(() => {
        if (comment.replyCount && comment.replyCount > 0 && !repliesLoaded) {
            loadInitialReplies();
        }
    }, [comment.id]);

    React.useEffect(() => {
        if (comment.children && comment.children.length > 0 && !repliesLoaded) {
            setReplies(comment.children);
            setTotalRepliesCount(comment.children.length);
            setRepliesLoaded(true);
        }
    }, [comment.children]);

    if (comment.status === 'BLOCKED') return null;

    if (comment.isDeleted || comment.status === 'DELETED_BY_USER' || comment.status === 'HIDDEN_BY_ADMIN') {
        return (
            <div className={`py-4 text-[13px] text-[#c0c0c0] italic ${isChild ? 'pl-10' : ''}`}>
                삭제된 댓글입니다.
            </div>
        );
    }

    const formatDate = (dateString: string) => {
        try {
            const d = new Date(dateString);
            const now = new Date();
            const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
            if (diff < 60) return '방금 전';
            if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
            if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
            return d.toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\. /g, '.').replace(/\.$/, '');
        } catch {
            return dateString;
        }
    };

    const handleReplySubmit = async (content: string) => {
        try {
            await onReply(comment.id, content);
            setIsReplying(false);
            setIsExpanded(true);
            await refreshReplies(true);
        } catch (error) {
            console.error("Failed to submit reply", error);
        }
    };

    const loadInitialReplies = async () => {
        try {
            const response = await getReplyList(comment.id, 1, 5);
            if (response) {
                const list = response.content || [];
                const email = localStorage.getItem("email");
                const mapped = list.map((item: any) => {
                    const likedKey = `comment_liked_${email || 'anonymous'}_${item.id}`;
                    return {
                        ...item,
                        isLiked: localStorage.getItem(likedKey) === 'true',
                        isMine: item.isMine || item.mine || (email && item.writerEmail === email) || false,
                    };
                });
                setReplies(mapped);
                setTotalRepliesCount(response.totalElements || mapped.length);
                setRepliesLoaded(true);
            }
        } catch (error) {
            console.error("Failed to load initial replies", error);
        }
    };

    const refreshReplies = async (keepExpanded: boolean = isExpanded) => {
        try {
            const size = keepExpanded ? 100 : 5;
            const response = await getReplyList(comment.id, 1, size);
            if (response) {
                const list = response.content || [];
                const email = localStorage.getItem("email");
                const mapped = list.map((item: any) => {
                    const likedKey = `comment_liked_${email || 'anonymous'}_${item.id}`;
                    return {
                        ...item,
                        isLiked: localStorage.getItem(likedKey) === 'true',
                        isMine: item.isMine || item.mine || (email && item.writerEmail === email) || false,
                    };
                });
                setReplies(mapped);
                setTotalRepliesCount(response.totalElements || mapped.length);
                setRepliesLoaded(true);
            }
        } catch (error) {
            console.error("Failed to refresh replies", error);
        }
    };

    const handleExpandReplies = async () => {
        if (replies.length < totalRepliesCount) {
            await refreshReplies(true);
        }
        setIsExpanded(true);
    };

    const handleCollapseReplies = () => {
        setIsExpanded(false);
    };

    const handleChildLike = async (childId: string) => {
        await onLike(childId);
        setReplies(prevReplies => {
            return prevReplies.map(r => {
                if (r.id === childId) {
                    const nextLiked = !r.isLiked;
                    return {
                        ...r,
                        isLiked: nextLiked,
                        likeCount: nextLiked ? r.likeCount + 1 : Math.max(0, r.likeCount - 1)
                    };
                }
                return r;
            });
        });
    };

    const hasReplies = totalRepliesCount > 0 || (replies && replies.length > 0);

    /* ── 자식 댓글 (답글) ── */
    if (isChild) {
        return (
            <div className="flex items-start gap-3 py-3">
                {/* 아바타 32px */}
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                    {comment.authorStatus && comment.authorProfileImg ? (
                        <img
                            src={comment.authorProfileImg.startsWith('data:') ? comment.authorProfileImg : `data:image/png;base64,${comment.authorProfileImg}`}
                            alt={comment.writerNickname}
                            className="w-full h-full object-cover"
                        />
                    ) : <DefaultAvatar />}
                </div>

                <div className="flex-1 min-w-0">
                    {/* 닉네임 + 작가 배지 + 날짜 + 메뉴 */}
                    <div className="flex justify-between items-start mb-1">
                        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5">
                            <span className="font-bold text-[#111827] text-[14px]">{comment.writerNickname}</span>
                            {comment.authorStatus && (
                                <span className="bg-[#d1fae5] text-[#047857] text-[12px] font-medium px-2 py-0.5 rounded-full leading-none">작가</span>
                            )}
                            <span className="text-[#6b7280] text-[13px]">{formatDate(comment.createdAt)}</span>
                        </div>
                        
                        {/* 답글 컨텍스트 메뉴 */}
                        <div className="relative flex-shrink-0 ml-2">
                            <button
                                onClick={() => setShowMenu(!showMenu)}
                                className="text-[#d1d5db] hover:text-[#9ca3af] p-1 transition-colors"
                            >
                                <DotsIcon />
                            </button>
                            {showMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                                    <div className="absolute right-0 top-7 w-20 bg-white border border-[#e5e7eb] rounded-[10px] shadow-md z-20 text-[13px] overflow-hidden">
                                        {!(comment.isMine || comment.mine) ? (
                                            <button onClick={() => { onReport(comment.id); setShowMenu(false); }}
                                                className="block w-full text-left px-3 py-2.5 text-[#6b7280] hover:bg-[#f5f5f5] transition-colors">
                                                신고
                                            </button>
                                        ) : (
                                            <button onClick={() => { onDelete(comment.id); setShowMenu(false); }}
                                                className="block w-full text-left px-3 py-2.5 text-[#e53935] hover:bg-[#fff5f5] transition-colors">
                                                삭제
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                    {/* 내용 */}
                    <p className="text-[#374151] text-[14px] leading-[22px] whitespace-pre-wrap break-words mb-2">
                        {comment.content}
                    </p>
                    {/* 좋아요 */}
                    <button
                        onClick={() => onLike(comment.id)}
                        className={`flex items-center gap-1 text-[13px] transition-colors ${comment.isLiked ? 'text-[#19cf7a]' : 'text-[#6b7280] hover:text-[#19cf7a]'}`}
                    >
                        <HeartIcon filled={comment.isLiked} />
                        {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
                    </button>
                </div>
            </div>
        );
    }

    /* ── 루트 댓글 ── */
    return (
        <div className="py-6">
            <div className="flex items-start gap-4 md:gap-3">
                {/* 아바타 36px */}
                <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0">
                    {comment.authorStatus && comment.authorProfileImg ? (
                        <img
                            src={comment.authorProfileImg.startsWith('data:') ? comment.authorProfileImg : `data:image/png;base64,${comment.authorProfileImg}`}
                            alt={comment.writerNickname}
                            className="w-full h-full object-cover"
                        />
                    ) : <DefaultAvatar />}
                </div>

                <div className="flex-1 min-w-0">
                    {/* 헤더: 닉네임 + 날짜 + 메뉴 */}
                    <div className="flex justify-between items-start mb-1.5">
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1">
                            <span className="font-bold text-[#111827] text-[16px] md:text-[15px]">{comment.writerNickname}</span>
                            {comment.authorStatus && (
                                <span className="bg-[#d1fae5] text-[#047857] text-[12px] font-medium px-2 py-0.5 rounded-full leading-none">작가</span>
                            )}
                            <span className="text-[#6b7280] text-[13px]">{formatDate(comment.createdAt)}</span>
                        </div>

                        {/* 점 3개 메뉴 */}
                        <div className="relative flex-shrink-0 ml-2">
                            <button
                                onClick={() => setShowMenu(!showMenu)}
                                className="text-[#d1d5db] hover:text-[#9ca3af] p-1 transition-colors"
                            >
                                <DotsIcon />
                            </button>
                            {showMenu && (
                                <>
                                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                                    <div className="absolute right-0 top-7 w-20 bg-white border border-[#e5e7eb] rounded-[10px] shadow-md z-20 text-[13px] overflow-hidden">
                                        {!(comment.isMine || comment.mine) ? (
                                            <button onClick={() => { onReport(comment.id); setShowMenu(false); }}
                                                className="block w-full text-left px-3 py-2.5 text-[#6b7280] hover:bg-[#f5f5f5] transition-colors">
                                                신고
                                            </button>
                                        ) : (
                                            <button onClick={() => { onDelete(comment.id); setShowMenu(false); }}
                                                className="block w-full text-left px-3 py-2.5 text-[#e53935] hover:bg-[#fff5f5] transition-colors">
                                                삭제
                                            </button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* 댓글 내용 */}
                    <p className="text-[#374151] text-[16px] md:text-[15px] leading-[26px] whitespace-pre-wrap break-words mb-3">
                        {comment.content}
                    </p>

                    {/* 액션 버튼 */}
                    <div className="flex items-center gap-4 pt-1">
                        <button
                            onClick={() => onLike(comment.id)}
                            className={`flex items-center gap-1 text-[14px] transition-colors ${comment.isLiked ? 'text-[#19cf7a]' : 'text-[#6b7280] hover:text-[#19cf7a]'}`}
                        >
                            <HeartIcon filled={comment.isLiked} />
                            {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
                        </button>

                        <button
                            onClick={() => setIsReplying(!isReplying)}
                            className="text-[14px] text-[#6b7280] hover:text-[#19cf7a] transition-colors"
                        >
                            답글 쓰기
                        </button>

                        {totalRepliesCount > 5 && (
                            <button
                                onClick={isExpanded ? handleCollapseReplies : handleExpandReplies}
                                className="text-[14px] text-[#19cf7a] font-medium hover:opacity-70 transition-opacity"
                            >
                                {isExpanded ? '접어두기' : '펼치기'}
                            </button>
                        )}
                    </div>

                    {/* 답글 입력창 */}
                    {isReplying && (
                        <div className="mt-4">
                            <CommentInput
                                onSubmit={handleReplySubmit}
                                placeholder="답글을 입력하세요."
                                buttonLabel="답글 등록"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* 답글 목록 */}
            {replies && replies.length > 0 && (
                <div className="mt-3 ml-[52px] md:ml-10 pl-4 border-l-2 border-[#f3f4f6] flex flex-col">
                    {(isExpanded ? replies : replies.slice(0, 5)).map(child => (
                        <CommentItem
                            key={child.id}
                            comment={child}
                            onReply={onReply}
                            onDelete={onDelete}
                            onReport={onReport}
                            onLike={handleChildLike}
                            isChild={true}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default CommentItem;
